import React from 'react';
import { identity, pipe, flatten } from 'ramda';
import { vyDefToComp } from '../../../../../Utils/ObjektVyUtils';
import addCaption from './Hocs/addCaption';
import {
  IObjektVy,
  IAttributeGroup,
  IBusinessCollectionProps,
} from '../../../../../Types/ObjektVy';
import { IClientBusinessModel } from '../../../../../Types/ClientModelTypes';
import ObjektVySida from './ObjektVySida/ObjektVySida';
import ObjektVyKolumner, {
  ObjektVyKolumn,
} from './ObjektVyKolumner/ObjektVyKolumner';

interface IProps extends IBusinessCollectionProps {
  bmod: IClientBusinessModel;
  /** lista av vyer (en sträng kan användas som genväg för att skapa en fältvy) */
  vyer: Array<Array<IObjektVy | IAttributeGroup | string>> | IObjektVy;
  caption: string;
  sidNr: number;
  leftMenuPresent?: boolean;
  fillPageSmall?: boolean;
}

interface IEnkelProps extends IProps, IBusinessCollectionProps {
  /** lista av vyer (en sträng kan användas som genväg för att skapa en fältvy) */
  vyer: IObjektVy;
}

interface IGrupperadProps extends IProps, IBusinessCollectionProps {
  /** lista av vyer (en sträng kan användas som genväg för att skapa en fältvy) */
  vyer: IObjektVy[][];
  hideCaption?: boolean;
}

const ObjektVySidaComp = (props: IProps) => {
  return Array.isArray(props.vyer) ? (
    <GrupperadObjektVySida {...(props as IGrupperadProps)} />
  ) : (
    <EnkelObjektVySida {...(props as IEnkelProps)} />
  );
};

/**
Komponent som visar en lista av objektvyer för samma objekt. 
Vyerna förväntas vara ObjektVyGrupper eller liknande
  */
const GrupperadObjektVySida = (props: IGrupperadProps) => {
  const colCount = props.vyer.length;

  const Comps = React.useMemo(
    () =>
      props.vyer.map((colDef, idx) => {
        const colComps = colDef.map(vyDef =>
          pipe(
            vyDefToComp(props.bmod),
            vyDef.info && vyDef.info.komponentMedRubrik
              ? identity
              : addCaption(
                  props.hideCaption
                    ? ''
                    : vyDef.def
                    ? vyDef.def(props.bmod).caption
                    : 'def saknas'
                ),
            Comp => (
              <Comp
                key={vyDef.id}
                id={props.id}
                config={{ parents: props.config?.parents ?? [] }}
              />
            )
          )(vyDef)
        );
        if (colCount === 1) {
          return colComps;
        }
        return (
          <ObjektVyKolumn fullPage={props.config?.fullPage} key={idx}>
            {colComps}
          </ObjektVyKolumn>
        );
      }),
    [
      colCount,
      props.bmod,
      props.config?.fullPage,
      props.config?.parents,
      props.hideCaption,
      props.id,
      props.vyer,
    ]
  );

  if (colCount === 1) {
    const Comp = Comps[0];
    return (
      <ObjektVySida
        caption={props.caption}
        sidNr={props.sidNr}
        collectionName={props.bmod.CollectionName}
        leftMenuPresent={props.leftMenuPresent}
        fillPageSmall={props.fillPageSmall}
      >
        {Comp}
      </ObjektVySida>
    );
  }

  return (
    <ObjektVySida
      caption={props.caption}
      sidNr={props.sidNr}
      collectionName={props.bmod.CollectionName}
      leftMenuPresent={props.leftMenuPresent}
      fillPageSmall={props.fillPageSmall}
    >
      <ObjektVyKolumner>{Comps}</ObjektVyKolumner>
    </ObjektVySida>
  );
};

/**
 * En sida som visar en enda vy. I första hand tänkt för en sida som visar en lista, men även andra komponenter som tar mycket plats kanske kan visas i en sådan här
 */
const EnkelObjektVySida = (props: IEnkelProps) => {
  const Comp = vyDefToComp(props.bmod)(props.vyer);
  return (
    <ObjektVySida
      caption={props.caption}
      sidNr={props.sidNr}
      collectionName={props.bmod.CollectionName}
      leftMenuPresent={props.leftMenuPresent}
      fillPageSmall={props.fillPageSmall}
    >
      <Comp
        key={`${(props.vyer as IObjektVy).type}-${
          (props.vyer as IObjektVy).id
        }-${props.id}`}
        id={props.id}
        config={{ parents: props.config?.parents ?? [] }}
      />
    </ObjektVySida>
  );
};

/**
En sida med en eller flera objektvyer. Om vyer är en lista förväntas det vara en lista av ObjektVyGrupper.
@deprecated Använd istället ObjektVySida direkt i vydefenitionen, och lägg in ObjektVyGrupper eller liknande i listan av vyer
*/
export const objektVySida = (
  /** typ för vyn, ska kunna användas som key för vyns komponent */
  typ: string,
  /** lista av vyer, en sträng kan användas som alternativ för att skapa en fältvy */
  vyer: IObjektVy[][] | IObjektVy,
  sidNr?: number,
  caption?: string,
  leftMenuPresent?: boolean,
  /** Sidans Content ska fylla upp hela sidan (Ingen margin/padding) på små skärmar */
  fillPageSmall?: boolean
): IObjektVy => {
  return {
    type: 'ObjektVy',
    id: typ,
    info: {},
    def: bmod => {
      const captionWithDefault =
        caption ||
        (!Array.isArray(vyer) && vyer.def(bmod).caption) ||
        bmod.RootType.SOLabel;
      return {
        caption: captionWithDefault,
        comp: (runtimeProps: IBusinessCollectionProps) => {
          return (
            <ObjektVySidaComp
              {...runtimeProps}
              vyer={vyer}
              bmod={bmod}
              caption={captionWithDefault}
              sidNr={sidNr ?? 1}
              leftMenuPresent={leftMenuPresent}
              fillPageSmall={fillPageSmall}
            />
          );
        },
        dataDependencies: Array.isArray(vyer)
          ? flatten(vyer).flatMap(vy => vy.def(bmod).dataDependencies ?? [])
          : vyer.def(bmod).dataDependencies,
      };
    },
  };
};
export default objektVySida;
